import { useEffect, type ReactNode } from 'react';

/**
 * Lightweight modal for the public form (e.g. showing long terms & conditions).
 * Dismisses on Escape or backdrop click; locks body scroll while open. Note: the
 * form is often embedded in an iframe, so the overlay spans the iframe's own
 * viewport — its body scrolls when the content is taller than that.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="rdr-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="rdr-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rdr-modal-head">
          <div className="rdr-modal-title">{title}</div>
          <button
            type="button"
            className="rdr-modal-x"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="rdr-modal-body">{children}</div>
        {footer && <div className="rdr-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
